import { alpha, createTheme, Components, Theme } from '@mui/material/styles';
import { ThemeMode } from 'types/config';
import { ColorProps } from 'types/extended';
import ThemeEfp from './theme/efp';
import getColors from 'utils/getColors';

declare module '@mui/material/Chip' {
    interface ChipClasses {
        light: string;
        combined: string;
    }
}

const PaletteEfp = (mode: ThemeMode) => {
    const paletteColor = ThemeEfp(mode);
    const SHAPE_PILL = '9999px';
    const isDark = mode === ThemeMode.DARK;

    const grey = paletteColor.grey;
    const dividerColor = alpha((isDark ? grey?.[700] : grey?.[200]) as string, 0.4);

    const additionalColors = {
        container: isDark ? '#545451' : '#fafafb',
        consoleBackground: '#232529',
        consoleText: '#FFFFFF',
        consoleHighlighted: '#8f8f2f',
    };

    const customSystem = {
        buttonRadius: SHAPE_PILL,
        cardRadius: '16px',
        chipRadius: '6px',
        useRingShadows: true,
        isSoftVariant: true,
    };

    const baseTheme = createTheme({
        palette: {
            mode,
            ...paletteColor,
        },
        customSystem,
    });

    const getChipColor = (color: ColorProps) => {
        const colors = getColors(baseTheme, color);
        return {
            '&.Mui-focusVisible': {
                outline: `2px solid ${colors.dark}`,
                outlineOffset: 2,
            },
        };
    };

    const getChipColorStyle = (color: ColorProps) => {
        const colors = getColors(baseTheme, color);
        return {
            color: colors.main,
            backgroundColor: colors.lighter,
            borderColor: alpha(colors.main, 0.25),
            '& .MuiChip-deleteIcon': {
                color: colors.main,
                '&:hover': { color: colors.light },
            },
        };
    };

    const components: Components<Omit<Theme, 'components'>> = {
        MuiButton: {
            defaultProps: { disableElevation: true },
            styleOverrides: {
                root: {
                    borderRadius: customSystem.buttonRadius,
                    textTransform: 'none',
                    fontWeight: 500,
                    boxShadow: 'none',
                    '&:hover': { boxShadow: 'none' },
                },
                containedPrimary: {
                    color: baseTheme.palette.primary.contrastText,
                    '&:hover': { backgroundColor: baseTheme.palette.primary.dark },
                },
                outlinedPrimary: customSystem.isSoftVariant
                    ? {
                          backgroundColor: baseTheme.palette.primary.lighter,
                          color: baseTheme.palette.primary.main,
                          border: 'none',
                          boxShadow: `0 0 0 1px ${alpha(baseTheme.palette.primary.main, 0.25)}`,
                          '&:hover': {
                              backgroundColor: alpha(baseTheme.palette.primary.main, 0.2),
                              border: 'none',
                              boxShadow: `0 0 0 1px ${alpha(baseTheme.palette.primary.main, 0.4)}`,
                          },
                      }
                    : {},
                outlinedSecondary: customSystem.isSoftVariant
                    ? {
                          backgroundColor: baseTheme.palette.secondary.lighter,
                          color: baseTheme.palette.secondary.main,
                          border: 'none',
                          boxShadow: `0 0 0 1px ${alpha(baseTheme.palette.secondary.main, 0.25)}`,
                          '&:hover': {
                              backgroundColor: alpha(baseTheme.palette.secondary.main, 0.2),
                              border: 'none',
                              boxShadow: `0 0 0 1px ${alpha(baseTheme.palette.secondary.main, 0.4)}`,
                          },
                      }
                    : {},
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: customSystem.cardRadius,
                    backgroundColor: grey?.['A100'] as string,
                    boxShadow:
                        customSystem.useRingShadows && !isDark
                            ? `0px 0px 0px 1px ${baseTheme.palette.grey[200]}`
                            : 'none',
                    backgroundImage: 'none',
                },
            },
        },
        MuiCardHeader: {
            styleOverrides: {
                title: { fontSize: '1.125rem', fontWeight: 600 },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    borderRadius: customSystem.chipRadius,
                    '&:active': { boxShadow: 'none' },
                    '&.MuiChip-colorPrimary': getChipColor('primary'),
                    '&.MuiChip-colorSecondary': getChipColor('secondary'),
                    '&.MuiChip-colorError': getChipColor('error'),
                    '&.MuiChip-colorInfo': getChipColor('info'),
                    '&.MuiChip-colorSuccess': getChipColor('success'),
                    '&.MuiChip-colorWarning': getChipColor('warning'),
                    '&.MuiChip-sizeLarge': {
                        fontSize: '1rem',
                        height: 40,
                    },
                },
                light: {
                    '&.MuiChip-lightPrimary': {
                        ...getChipColorStyle('primary'),
                        ...(customSystem.useRingShadows && {
                            boxShadow: `0 0 0 1px ${alpha(baseTheme.palette.primary.main, 0.25)}`,
                        }),
                    },
                    '&.MuiChip-lightSecondary': {
                        ...getChipColorStyle('secondary'),
                        ...(customSystem.useRingShadows && {
                            boxShadow: `0 0 0 1px ${alpha(baseTheme.palette.secondary.main, 0.25)}`,
                        }),
                    },
                },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    borderRadius: customSystem.buttonRadius,
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: dividerColor,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: baseTheme.palette.primary.main,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderWidth: '1px',
                        borderColor: baseTheme.palette.primary.main,
                    },
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    borderBottom: `1px solid ${dividerColor}`,
                },
                head: { backgroundColor: 'transparent', fontWeight: 600 },
            },
        },
        MuiTableContainer: {
            styleOverrides: {
                root: { boxShadow: 'none', border: 'none', borderRadius: '16px' },
            },
        },
        MuiAppBar: {
            styleOverrides: {
                root: {
                    backgroundColor: grey?.['A50'] as string,
                    backgroundImage: 'none',
                },
            },
        },
        MuiDrawer: {
            styleOverrides: {
                paper: {
                    backgroundColor: grey?.['A50'] as string,
                    backgroundImage: 'none',
                },
            },
        },
    };

    return createTheme({
        customSystem,
        palette: {
            mode,
            common: { black: '#000', white: '#fff' },
            ...paletteColor,
            text: {
                primary: grey?.['A700'] as string,
                secondary: grey?.[500] as string,
            },
            action: {
                disabled: grey?.[300],
            },
            divider: dividerColor,
            background: {
                default: grey?.['A50'] as string,
                paper: grey?.['A100'] as string,
            },
            ...additionalColors,
        },
        components,
    });
};

export default PaletteEfp;
